import { useEffect, useState, useRef } from 'react';
import {
  Box,
  Popover,
  Stack,
  Typography,
  IconButton,
  DialogContent,
  DialogTitle,
  Tooltip,
  Divider,
} from '@mui/material';
import { Link as LinkIcon, AcUnit, Print, AltRoute, Visibility } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { OrderType } from '../../../interfaces/delivery-company/orders';
import { DeliveryState } from '../../../enums/orders';
import RerouteOrderDialog from '../reroute-order-dialog';
import CopyLinkButton from '../copy-link-button/copy-link-button';
import CustomDialog from '../custom-dialog/custom-dialog';
import { PrintWaybillsMultiSizes } from '../bulk-action/print-waybills';
import LebanonMap, { LocationType } from '../lebanon-map/lebanon-map';
import { Workflow } from '../../../enums/orders';

interface OrderIdWithPopoverProps {
  order: OrderType;
  /** Optional callback when order is updated (e.g., after rerouting) */
  onOrderUpdated?: () => void;
}

export const OrderIdWithPopover = ({ order, onOrderUpdated }: OrderIdWithPopoverProps) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [printingWaybill, setPrintingWaybill] = useState<boolean>(false);
  const [rerouteDialogOpen, setRerouteDialogOpen] = useState<boolean>(false);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Check if order is eligible for rerouting (at warehouse)
  const canReroute = order.delivery_state === DeliveryState.AT_WAREHOUSE;

  const open = Boolean(anchorEl);
  const trackingLink = `${window.location.origin}/tracking/${order.tracking_hash}`;

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    // Clear any existing timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    // Set a longer timeout to allow mouse to move to popover
    hoverTimeoutRef.current = setTimeout(() => {
      setAnchorEl(null);
    }, 300);
  };

  const handlePopoverMouseEnter = () => {
    // Clear timeout when mouse enters popover
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  };

  const handlePopoverMouseLeave = () => {
    // Close popover when mouse leaves it
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    setAnchorEl(null);
  };

  const handleShowMore = () => {
    navigate(`/order/${order.id}`);
    handlePopoverClose();
  };

  const handleOrderIdClick = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate(`/order/${order.id}`);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  return (
    <>
      <Box
        onMouseEnter={handlePopoverOpen}
        onMouseLeave={handlePopoverClose}
        sx={{ display: 'inline-block', position: 'relative' }}
      >
        <Box
          component="a"
          href={`/order/${order.id}`}
          onClick={handleOrderIdClick}
          sx={{
            textDecoration: 'none',
            color: 'inherit',
            cursor: 'pointer',
            '&:hover': {
              textDecoration: 'underline',
            },
          }}
        >
          <Typography
            component="span"
            variant="body2"
            sx={{ fontWeight: 600, color: 'primary.main' }}
          >
            {order.order_id}
          </Typography>
          {order.reference_id && order.reference_id.length > 0 && (
            <Typography
              component="span"
              variant="caption"
              sx={{ display: 'block', color: 'text.secondary' }}
            >
              ({order.reference_id})
            </Typography>
          )}
        </Box>
        {order.is_frozen && (
          <Box sx={{ display: 'inline-block', ml: 0.5, verticalAlign: 'middle' }}>
            <AcUnit color="primary" fontSize="small" />
          </Box>
        )}
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        disableRestoreFocus
        sx={{
          pointerEvents: 'none',
        }}
        slotProps={{
          paper: {
            onMouseEnter: handlePopoverMouseEnter,
            onMouseLeave: handlePopoverMouseLeave,
            sx: {
              marginLeft: '8px',
              pointerEvents: 'auto',
              maxWidth: 240,
              width: 240,
              p: 0.75,
              borderRadius: 2,
            },
          },
        }}
      >
        <Stack spacing={0}>
          {/* Header */}
          <Box sx={{ textAlign: 'center' }}>
            {order.reference_id && (
              <Typography variant="caption" color="text.secondary">
                Ref: {order.reference_id}
              </Typography>
            )}
          </Box>
          
          <Divider sx={{ 
            '&::before, &::after': { 
              borderColor: 'primary.light',
              borderWidth: 1,
            }
          }}>
            <Typography variant="caption" color="text.secondary" sx={{ px: 1 }}>
              Quick Actions
            </Typography>
          </Divider>
          
          {/* Action Buttons */}
          <Stack direction="row" spacing={1} justifyContent="center">
            {canReroute && (
              <Tooltip 
                title={order.next_leg 
                  ? `Reroute (scheduled for transfer to ${order.next_leg.destination?.name})`
                  : "Reroute order"
                }
                arrow
              >
                <IconButton 
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setRerouteDialogOpen(true);
                  }}
                  sx={{
                    color: order.next_leg ? 'warning.main' : 'primary.main',
                  }}
                >
                  <AltRoute />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Print Waybill" arrow>
              <IconButton 
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setPrintingWaybill(true);
                }}
              >
                <Print color="primary" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Copy Tracking Link" arrow>
              <span>
                <CopyLinkButton
                  icon={<LinkIcon color="primary" />}
                  link={trackingLink}
                />
              </span>
            </Tooltip>
            <Tooltip title="View Order Details" arrow>
              <IconButton
                size="small"
                onClick={handleShowMore}
              >
                <Visibility color="primary" />
              </IconButton>
            </Tooltip>
          </Stack>
          
          <Divider sx={{ 
            '&::before, &::after': { 
              borderColor: 'primary.light',
              borderWidth: 1,
            }
          }}>
            <Typography variant="caption" color="text.secondary" sx={{ px: 1 }}>
              Destination
            </Typography>
          </Divider>
          
          {/* Destination Map */}
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <LebanonMap
              locations={[{
                id: 'destination',
                label: order.workflow === Workflow.RETURN 
                  ? (order.merchant?.name || 'Merchant')
                  : (order.customer?.name || 'Customer'),
                area: order.workflow === Workflow.RETURN 
                  ? order.merchant_location?.area 
                  : order.customer_location?.area,
                type: order.workflow === Workflow.RETURN 
                  ? LocationType.SHOP 
                  : LocationType.HOME,
              }]}
              width={150}
              height={200}
              scale={5000}
              showPartitions={false}
              showDistricts={false}
              useStaticMap
            />
          </Box>
        </Stack>
      </Popover>

      {/* Waybill Print Dialog */}
      {printingWaybill && (
        <CustomDialog open={true} onClose={() => setPrintingWaybill(false)}>
          <DialogTitle>
            <Typography align="center" variant="h5">
              Order #{order.order_id}
            </Typography>
          </DialogTitle>
          <DialogContent>
            <PrintWaybillsMultiSizes
              orders={[order]}
              onClose={() => setPrintingWaybill(false)}
            />
          </DialogContent>
        </CustomDialog>
      )}

      {/* Reroute Dialog */}
      <RerouteOrderDialog
        orders={[order]}
        open={rerouteDialogOpen}
        onClose={() => setRerouteDialogOpen(false)}
        onSuccess={() => {
          setRerouteDialogOpen(false);
          onOrderUpdated?.();
        }}
      />
    </>
  );
};

